import { useMemo, useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Database, Info, RotateCcw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { EmptyBlock, FetchingDot, SectionCard } from "../../components/primitives";
import { formatCount, formatStamp } from "../../format";
import { useMonitoring } from "../../monitoring-context";
import type { EventFilters, EventsResponse, MonitoringEvent } from "../../types";
import { QueryBody } from "../requests/bits";
import { LevelToggle } from "./bits";
import { EventRow } from "./event-row";
import {
  EVENT_LIMITS,
  EVENT_RANGES,
  EVENT_SOURCES,
  filterInBrowser,
  isDefaultFilters,
  MEMORY_RING_SIZE,
  sourceLabel,
  type EventRange,
  type LogFilterState,
} from "./utils";

const ALL_SOURCES = "__all";
const PAGE = 100;

/** The event log, newest first, with its filters (levels, source, text, pizza, time range, limit). */
export function EventLog({
  id,
  state,
  onChange,
  onReset,
  query,
  filters,
  branchNames,
}: {
  id: string;
  state: LogFilterState;
  onChange: (patch: Partial<LogFilterState>) => void;
  onReset: () => void;
  query: UseQueryResult<EventsResponse>;
  filters: EventFilters;
  branchNames: Map<string, string>;
}) {
  const { t, tp } = useLocalization();
  const { window } = useMonitoring();

  const sources = useMemo(() => {
    const known = new Set<string>(EVENT_SOURCES);
    for (const event of query.data?.events ?? []) known.add(event.source);
    if (state.source) known.add(state.source);
    return [...known];
  }, [query.data, state.source]);

  return (
    <SectionCard
      id={id}
      className="scroll-mt-28"
      title={t("monitoring.events.log.title")}
      description={t("monitoring.events.log.description")}
      actions={<FetchingDot active={query.isFetching} />}
    >
      <div className="mb-3 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <LevelToggle
            value={state.levels}
            onChange={(levels) => onChange({ levels })}
            label={t("monitoring.events.log.levels")}
          />
          <Select
            value={state.source || ALL_SOURCES}
            onValueChange={(value) => onChange({ source: value === ALL_SOURCES ? "" : value })}
          >
            <SelectTrigger className="h-8 w-full text-xs sm:w-44" aria-label={t("monitoring.events.log.source")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SOURCES} className="text-xs">{t("monitoring.events.log.allSources")}</SelectItem>
              {sources.map((source) => (
                <SelectItem key={source} value={source} className="text-xs">
                  {sourceLabel(source)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={state.range} onValueChange={(value) => onChange({ range: value as EventRange })}>
            <SelectTrigger className="h-8 w-full text-xs sm:w-56" aria-label={t("monitoring.events.log.range")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_RANGES.map((range) => (
                <SelectItem key={range} value={range} className="text-xs">
                  {range === "window"
                    ? t("monitoring.events.range.window", { window: t(`monitoring.window.long.${window}`) })
                    : t(`monitoring.events.range.${range}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground" id={`${id}-limit`}>
              {t("monitoring.events.log.limit")}
            </span>
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              value={String(state.limit)}
              onValueChange={(value) => value && onChange({ limit: Number(value) })}
              aria-labelledby={`${id}-limit`}
              className="gap-0.5"
            >
              {EVENT_LIMITS.map((limit) => (
                <ToggleGroupItem key={limit} value={String(limit)} className="h-8 px-2 font-mono text-xs">
                  {limit}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[12rem] flex-[2_1_16rem]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              type="search"
              value={state.q}
              onChange={(event) => onChange({ q: event.target.value })}
              placeholder={t("monitoring.events.log.search")}
              aria-label={t("monitoring.events.log.search")}
              className="h-8 pl-8 text-xs md:text-xs"
            />
          </div>
          <Input
            value={state.pizzaId}
            onChange={(event) => onChange({ pizzaId: event.target.value })}
            placeholder={t("monitoring.events.log.pizzaId")}
            aria-label={t("monitoring.events.log.pizzaId")}
            spellCheck={false}
            className="h-8 min-w-[10rem] flex-[1_1_12rem] font-mono text-xs md:text-xs"
          />
          {state.fingerprint && (
            <span className="inline-flex h-8 min-w-0 max-w-full items-center gap-1.5 rounded-md border bg-muted/40 pl-2 pr-1 text-xs">
              <span className="shrink-0 text-muted-foreground">{t("monitoring.events.log.group")}</span>
              <span className="min-w-0 truncate font-mono" title={state.fingerprint}>{state.fingerprint}</span>
              <button
                type="button"
                onClick={() => onChange({ fingerprint: "" })}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={t("monitoring.events.log.clearGroup")}
                title={t("monitoring.events.log.clearGroup")}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </span>
          )}
          {!isDefaultFilters(state) && (
            <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs" onClick={onReset}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              {t("monitoring.events.log.reset")}
            </Button>
          )}
        </div>
      </div>

      <QueryBody query={query} height={320}>
        {(data) => {
          const memory = data.source === "memory";
          const events = memory ? filterInBrowser(data.events, filters) : data.events;
          const limitReached = !memory && data.events.length >= state.limit;
          const signature = JSON.stringify(filters);
          return (
            <>
              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" role="status">
                <span className="font-medium text-foreground/90">
                  {tp("monitoring.events.log.count", events.length, { count: formatCount(events.length) })}
                </span>
                {!!filters.sinceMs && <span>{t("monitoring.events.log.since", { time: formatStamp(filters.sinceMs) })}</span>}
                {limitReached && (
                  <span className="inline-flex items-center gap-1">
                    <Info className="h-3 w-3" aria-hidden />
                    {t("monitoring.events.log.limitReached", { limit: state.limit })}
                  </span>
                )}
              </div>
              {memory && (
                <div className="mb-3 flex items-start gap-2 rounded-md border border-dashed px-3 py-2 text-[11px] text-muted-foreground">
                  <Database className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="break-words">{t("monitoring.events.log.memory", { size: MEMORY_RING_SIZE })}</span>
                </div>
              )}
              {events.length ? (
                <EventList key={signature} events={events} branchNames={branchNames} />
              ) : (
                <EmptyBlock>
                  {isDefaultFilters(state) ? t("monitoring.events.log.empty") : t("monitoring.events.log.noMatch")}
                </EmptyBlock>
              )}
            </>
          );
        }}
      </QueryBody>
    </SectionCard>
  );
}

/** The rows, 100 at a time (a long log stays light while every row's relative time ticks). */
function EventList({ events, branchNames }: { events: MonitoringEvent[]; branchNames: Map<string, string> }) {
  const { t } = useLocalization();
  const [visible, setVisible] = useState(PAGE);
  const shown = events.slice(0, visible);
  const left = events.length - shown.length;
  return (
    <>
      <ul className="flex flex-col divide-y divide-border/60 rounded-md border">
        {shown.map((event) => (
          <EventRow
            key={event.id}
            event={event}
            branchName={event.branch_id ? branchNames.get(event.branch_id) ?? null : null}
          />
        ))}
      </ul>
      {left > 0 && (
        <div className="mt-2 flex justify-center">
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => setVisible(visible + PAGE)}>
            {t("monitoring.events.log.showMore", { count: formatCount(Math.min(PAGE, left)), left: formatCount(left) })}
          </Button>
        </div>
      )}
    </>
  );
}
